import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Code } from "@connectrpc/connect";
import { createConnectQueryKey, useMutation, useQuery } from "@connectrpc/connect-query";
import { useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";

import { serverNow } from "@yatayat/core/clock";
import { formatDate, formatNepalTime, nepalIsoDate } from "@yatayat/core/date";
import { formatNumber, formatPaisa } from "@yatayat/core/money";
import { BookingService, PaymentMethod, type TripOffer } from "@yatayat/gen/yatayat/booking/v1/booking_pb";

import { PhoneSignIn } from "~/components/PhoneSignIn";
import { Loading } from "~/components/QueryState";
import { SeatGrid } from "~/components/SeatGrid";
import { OfficialFareBadge, PermitVerifiedBadge } from "~/components/TripBadges";
import { useI18n, type MessageKey } from "~/lib/i18n";
import { formatCountdown, localDigits, placeName, vehicleClassLabels } from "~/lib/labels";
import { useTripChanges } from "~/lib/live";
import { errorCode, retryTransient } from "~/lib/rpc/errors";
import { placeCodePattern, searchString } from "~/lib/search";
import { useRefreshSession, useSession } from "~/lib/session";
import { ui } from "~/lib/ui";

type TripSearch = { from: string; to: string };

export const Route = createFileRoute("/book/$tripId")({
  validateSearch: (search: Record<string, unknown>): TripSearch => ({
    from: searchString(search.from) ?? "",
    to: searchString(search.to) ?? "",
  }),
  component: TripPage,
});

function TripPage() {
  const { tripId } = Route.useParams();
  const { from, to } = Route.useSearch();
  const { t } = useI18n();

  if (!placeCodePattern.test(from) || !placeCodePattern.test(to) || from === to) {
    return (
      <div className="mx-auto max-w-xl space-y-3">
        <p className="text-slate-600 dark:text-slate-400">{t("missingStretch")}</p>
        <Link to="/book" className="text-sm text-ink underline dark:text-ink-dark">
          {t("bookSeat")}
        </Link>
      </div>
    );
  }

  // A new trip or stretch starts the flow afresh.
  return <SeatBooking key={`${tripId}:${from}:${to}`} tripId={tripId} from={from} to={to} />;
}

type Hold = { ticketId: string; holdToken: string; seatNo: string; expiresAtMs: number };

const holdMs = 10 * 60 * 1000;
const paymentMethods: { value: PaymentMethod; label: MessageKey }[] = [
  { value: PaymentMethod.WALLET, label: "payWallet" },
  { value: PaymentMethod.QR, label: "payQr" },
  { value: PaymentMethod.BANK, label: "payBank" },
];

function SeatBooking({ tripId, from, to }: { tripId: string; from: string; to: string }) {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const session = useSession();
  const refreshSession = useRefreshSession();
  const seatMap = useQuery(
    BookingService.method.getSeatMap,
    { tripId, fromCode: from, toCode: to },
    { retry: retryTransient },
  );
  const refetchSeats = seatMap.refetch;
  // A seat sold at a counter or by a partner greys out here within a second.
  useTripChanges(tripId, () => void refetchSeats());

  const [hold, setHoldState] = useState<Hold | null>(() => loadHold(tripId));
  const [seatNotice, setSeatNotice] = useState<MessageKey | null>(null);
  const [payNotice, setPayNotice] = useState<MessageKey | null>(null);
  const [signedOutMidway, setSignedOutMidway] = useState(false);
  const [payment, setPayment] = useState(PaymentMethod.WALLET);

  const holdSeat = useMutation(BookingService.method.holdSeat);
  const releaseHold = useMutation(BookingService.method.releaseHold);
  const confirm = useMutation(BookingService.method.confirmBooking);

  const setHold = useCallback(
    (next: Hold | null) => {
      setHoldState(next);
      storeHold(tripId, next);
    },
    [tripId],
  );

  const expire = useCallback(() => {
    setHold(null);
    setSeatNotice("holdExpired");
    setPayNotice(null);
    void refetchSeats();
  }, [setHold, refetchSeats]);

  const remaining = useCountdown(hold?.expiresAtMs);
  useEffect(() => {
    // Let a payment in flight finish; the server has the last word on expiry.
    if (hold && remaining === 0 && !confirm.isPending) expire();
  }, [hold, remaining, confirm.isPending, expire]);

  function pickSeat(seatNo: string) {
    setSeatNotice(null);
    holdSeat.mutate(
      { tripId, seatNo, fromCode: from, toCode: to },
      {
        onSuccess: (res) => {
          // Count down on the api's clock as last synced, trusting its deadline unless the clocks clearly disagree.
          const ttl = Number(res.expiresAtMs) - serverNow();
          setHold({
            ticketId: res.ticketId,
            holdToken: res.holdToken,
            seatNo: res.seatNo || seatNo,
            expiresAtMs: serverNow() + (ttl > 0 && ttl <= holdMs ? ttl : holdMs),
          });
          void refetchSeats();
        },
        onError: (err) => {
          setSeatNotice(errorCode(err) === Code.AlreadyExists ? "seatJustTaken" : "somethingWrong");
          void refetchSeats();
        },
      },
    );
  }

  function release() {
    if (!hold) return;
    setSeatNotice(null);
    setPayNotice(null);
    releaseHold.mutate(
      { ticketId: hold.ticketId, holdToken: hold.holdToken },
      {
        onSuccess: () => {
          setHold(null);
          void refetchSeats();
        },
        onError: (err) => {
          const code = errorCode(err);
          // The server no longer has the hold, so there is nothing left to release.
          if (code === Code.NotFound || code === Code.FailedPrecondition) {
            setHold(null);
            void refetchSeats();
          } else {
            setSeatNotice("somethingWrong");
          }
        },
      },
    );
  }

  function pay(e: FormEvent) {
    e.preventDefault();
    if (!hold) return;
    setPayNotice(null);
    confirm.mutate(
      { ticketId: hold.ticketId, holdToken: hold.holdToken, paymentMethod: payment },
      {
        onSuccess: (res) => {
          storeHold(tripId, null);
          void queryClient.invalidateQueries({
            queryKey: createConnectQueryKey({ schema: BookingService.method.listMyTickets, cardinality: undefined }),
          });
          void navigate({ to: "/tickets/$ticketId", params: { ticketId: res.ticket?.id || hold.ticketId } });
        },
        onError: (err) => {
          switch (errorCode(err)) {
            case Code.FailedPrecondition:
            case Code.NotFound:
              expire();
              break;
            case Code.Unauthenticated:
              setSignedOutMidway(true);
              void refreshSession();
              break;
            default:
              setPayNotice("somethingWrong");
          }
        },
      },
    );
  }

  const backLink = (search: { from: string; to: string; date?: string }) => (
    <Link to="/book" search={search} className="text-sm text-ink underline-offset-2 hover:underline dark:text-ink-dark">
      <span aria-hidden="true">← </span>
      {t("backToResults")}
    </Link>
  );

  if (!seatMap.data) {
    return (
      <div className="mx-auto max-w-xl space-y-3">
        {backLink({ from, to })}
        {seatMap.isError ? (
          <p className="text-brand dark:text-brand-dark">{t(seatMapError(seatMap.error))}</p>
        ) : (
          <Loading />
        )}
      </div>
    );
  }

  const trip = seatMap.data.trip;
  const phone = session.data?.session?.phoneMasked;

  return (
    <div className="mx-auto max-w-xl space-y-6">
      {backLink({ from, to, date: trip ? nepalIsoDate(Number(trip.departsAtMs)) : undefined })}
      {trip && <TripSummary trip={trip} />}

      <Step n={1} title={t("stepSeat")}>
        <SeatGrid
          seats={seatMap.data.seats}
          selectedSeat={hold?.seatNo}
          pendingSeat={holdSeat.isPending ? holdSeat.variables?.seatNo : undefined}
          disabled={hold !== null || holdSeat.isPending}
          onPick={pickSeat}
        />
        <div role="status">
          {seatNotice && <p className="mt-3 text-sm text-brand dark:text-brand-dark">{t(seatNotice)}</p>}
        </div>
        {hold && (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-ink/20 bg-ink/5 p-3 dark:border-ink-dark/30 dark:bg-ink-dark/10">
            <div>
              <p className="font-medium">{t("seatHeld", { seat: localDigits(hold.seatNo, lang) })}</p>
              <p role="timer" className="text-sm tabular-nums text-slate-600 dark:text-slate-400">
                {t("timeLeft", { time: formatCountdown(remaining ?? 0, lang) })}
              </p>
            </div>
            <button
              type="button"
              onClick={release}
              disabled={releaseHold.isPending || confirm.isPending}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-100 disabled:opacity-60 dark:border-slate-700 dark:hover:bg-slate-900"
            >
              {t("chooseAnotherSeat")}
            </button>
          </div>
        )}
      </Step>

      {hold && !session.isPending && (
        <Step n={2} title={t("stepSignIn")}>
          {phone ? (
            <p className="text-sm text-emerald-700 dark:text-emerald-400">
              <span aria-hidden="true">✓ </span>
              {t("signedInAs", { phone: localDigits(phone, lang) })}
            </p>
          ) : (
            <div className="space-y-3">
              {signedOutMidway && (
                <p role="alert" className="text-sm text-brand dark:text-brand-dark">
                  {t("sessionEnded")}
                </p>
              )}
              <PhoneSignIn />
            </div>
          )}
        </Step>
      )}

      {hold && phone && (
        <Step n={3} title={t("stepPay")}>
          <form className="space-y-4" onSubmit={pay}>
            <fieldset>
              <legend className="mb-1 text-sm font-medium">{t("paymentMethod")}</legend>
              <div className="flex flex-wrap gap-2">
                {paymentMethods.map((m) => (
                  <label
                    key={m.value}
                    className={`cursor-pointer rounded-md border px-4 py-2 text-sm has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ink ${
                      payment === m.value
                        ? "border-ink bg-ink text-white dark:border-ink-dark dark:bg-ink-dark dark:text-slate-950"
                        : "border-slate-300 dark:border-slate-700"
                    }`}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      className="sr-only"
                      checked={payment === m.value}
                      onChange={() => setPayment(m.value)}
                    />
                    {t(m.label)}
                  </label>
                ))}
              </div>
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{t("paySimulated")}</p>
            </fieldset>
            <button
              type="submit"
              disabled={confirm.isPending}
              className="w-full rounded-md bg-brand px-4 py-2.5 font-medium text-white disabled:opacity-60 sm:w-auto dark:bg-brand-dark"
            >
              {confirm.isPending
                ? t("paying")
                : t("payAmount", { amount: formatPaisa(trip?.officialFare?.paisa ?? 0n, lang) })}
            </button>
            {payNotice && (
              <p role="alert" className="text-sm text-brand dark:text-brand-dark">
                {t(payNotice)}
              </p>
            )}
          </form>
        </Step>
      )}
    </div>
  );
}

function TripSummary({ trip }: { trip: TripOffer }) {
  const { t, lang } = useI18n();
  const departs = Number(trip.departsAtMs);

  return (
    <section className="rounded-xl border border-slate-200 p-4 dark:border-slate-800">
      <p className="text-sm text-slate-600 dark:text-slate-400">
        {formatDate(departs, lang)} · {t("departs", { time: formatNepalTime(departs, lang) })}
      </p>
      <h1 className={`mt-1 ${ui.h1}`}>
        {placeName(trip.from, lang)} → {placeName(trip.to, lang)}
      </h1>
      <p className="text-sm text-slate-600 dark:text-slate-400">
        {trip.operatorName?.[lang]} · {t(vehicleClassLabels[trip.vehicleClass])}
        {trip.electric && ` · ${t("electric")}`}
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="text-xl font-semibold">{formatPaisa(trip.officialFare?.paisa ?? 0n, lang)}</span>
        <OfficialFareBadge />
        {trip.permitVerified && <PermitVerifiedBadge />}
      </div>
    </section>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  const { lang } = useI18n();

  return (
    <section>
      <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold">
        <span
          aria-hidden="true"
          className="flex h-7 w-7 items-center justify-center rounded-full bg-ink text-sm text-white dark:bg-ink-dark dark:text-slate-950"
        >
          {formatNumber(n, lang)}
        </span>
        {title}
      </h2>
      {children}
    </section>
  );
}

/** Milliseconds left until the deadline, ticking every second; undefined without one. */
function useCountdown(deadline: number | undefined): number | undefined {
  const [now, setNow] = useState(serverNow);

  useEffect(() => {
    if (deadline === undefined) return;
    setNow(serverNow());
    const id = window.setInterval(() => setNow(serverNow()), 1000);
    return () => window.clearInterval(id);
  }, [deadline]);

  return deadline === undefined ? undefined : Math.max(0, deadline - now);
}

function seatMapError(err: unknown): MessageKey {
  switch (errorCode(err)) {
    case Code.NotFound:
    case Code.FailedPrecondition:
      return "tripNotFound";
    case Code.InvalidArgument:
      return "missingStretch";
    default:
      return "somethingWrong";
  }
}

const holdKey = (tripId: string) => `yatayat.hold.${tripId}`;

// Session storage keeps a hold across a reload. It can throw in private windows or
// when site data is blocked; the hold then lasts as long as the page.
function loadHold(tripId: string): Hold | null {
  try {
    const raw = sessionStorage.getItem(holdKey(tripId));
    if (!raw) return null;
    const h = JSON.parse(raw) as Partial<Hold>;
    if (
      typeof h.ticketId === "string" &&
      typeof h.holdToken === "string" &&
      typeof h.seatNo === "string" &&
      typeof h.expiresAtMs === "number" &&
      h.expiresAtMs > serverNow()
    ) {
      return h as Hold;
    }
  } catch {
    // Treated as no hold.
  }
  return null;
}

function storeHold(tripId: string, hold: Hold | null) {
  try {
    if (hold) sessionStorage.setItem(holdKey(tripId), JSON.stringify(hold));
    else sessionStorage.removeItem(holdKey(tripId));
  } catch {
    // See loadHold.
  }
}
